import { BrowserCoachingAiServicesConfig } from '@ampsec/platform-client';

export enum BrowserCoachingSubject {
  AI_SERVICE_COACHING = 'AI_SERVICE_COACHING',
  DOCUMENT_SHARING_COACHING_GOOGLE = 'DOCUMENT_SHARING_COACHING_GOOGLE',
  DOCUMENT_SHARING_COACHING_MICROSOFT = 'DOCUMENT_SHARING_COACHING_MICROSOFT',
  AI_SERVICE_SIGNALS = "AI_SERVICE_SIGNALS"
}

export type CoachingSubject = {
    subject: BrowserCoachingSubject;
    name: string;
    description: string;
    enabled: boolean;
}

export const getBrowserCoachingSubjects = (
  t: (key: string, defaultValue?: string | undefined) => string,
  activeSubjects: string[] = [],
): CoachingSubject[] => {
  return [
    {
      subject: BrowserCoachingSubject.AI_SERVICE_COACHING,
      name: t('AI_SERVICE_COACHING_TITLE'),
      description: t('AI_SERVICE_COACHING_DESCRIPTION'),
      enabled: activeSubjects.includes(BrowserCoachingSubject.AI_SERVICE_COACHING),
    },
    {
      subject: BrowserCoachingSubject.DOCUMENT_SHARING_COACHING_GOOGLE,
      name: t('DOCUMENT_SHARING_COACHING_GOOGLE_TITLE'),
      description: t('DOCUMENT_SHARING_COACHING_GOOGLE_DESCRIPTION'),
      enabled: activeSubjects.includes(BrowserCoachingSubject.DOCUMENT_SHARING_COACHING_GOOGLE),
    },
    {
      subject: BrowserCoachingSubject.AI_SERVICE_SIGNALS,
      name: t('AI_SERVICE_SIGNALS_TITLE', 'Receive AI Service Signals'),
      description: t('AI_SERVICE_SIGNALS_DESCRIPTION', 'Toggle AI service use monitoring on/off'),
      enabled: activeSubjects.includes(BrowserCoachingSubject.AI_SERVICE_SIGNALS),
    },
    // {
    //   subject: AICoachingSubject.DOCUMENT_SHARING_COACHING_MICROSOFT,
    //   name: t('DOCUMENT_SHARING_COACHING_MICROSOFT_TITLE'),
    //   description: t('DOCUMENT_SHARING_COACHING_MICROSOFT_DESCRIPTION'),
    //   enabled: activeSubjects.includes(AICoachingSubject.DOCUMENT_SHARING_COACHING_MICROSOFT),
    // }
  ];
};

const DEFAULT_LIMP_MODE_TEXT =
  'Treat every AI prompt like it is public. Never paste secrets or sensitive data no credentials (API keys, passwords), customer info, unreleased code/architecture, internal URLs, or financials.';

const DEFAULT_BEST_PRACTICES_HTML = `<h3>Ampy's AI Best Practices</h3>
<ul style="list-style: disc; padding-left: 20px; margin-top: 8px;">
  <li>
    Treat every AI prompt like it’s public. Never paste secrets or sensitive data—no credentials
    (API keys, passwords), customer info, unreleased code/architecture, internal URLs, or
    financials.
  </li>

  <li>
    Share only the <strong>minimum necessary</strong> context: summarize, use placeholders like
    <span class="ampsec_ext_code">&lt;API_KEY&gt;</span> and fake data, and stick to
    company-approved AI tools/workspaces.
  </li>

  <li>
    Before prompting: <span class="ampsec_ext_bold">classify → sanitize → ask</span>. Classify
    the material (only Public/Internal is okay). Sanitize by removing identifiers, tokens, logs,
    and exact numbers. Ask for patterns (“How to design OAuth refresh flow?”) instead of pasting
    your code. If you slip up, rotate keys immediately and report it per incident procedure.
  </li>
</ul>`;

export const DEFAULT_AI_SERVICES_CONFIG: BrowserCoachingAiServicesConfig = {
  services: [
    {
      id: 'chatgpt',
      displayName: 'ChatGPT',
      pattern: 'https://chatgpt.com',
      currentStatus: 'APPROVED',
      templates: {
        APPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            chatgpt: 'Ask anything in chatgpt... just no secrets, keys, or customer data! 🕵',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        DISAPPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            chatgpt: 'ChatGPT is not approved for work use. 🚫',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        UNDEFINED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            chatgpt: 'Policy for ChatGPT is undefined. ⚠️',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
      },
    },
    {
      id: 'gemini',
      displayName: 'Gemini',
      pattern: 'https://gemini.google.com/app',
      currentStatus: 'APPROVED',
      templates: {
        APPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            gemini: 'Enter a prompt for Gemini... just no secrets, keys, or customer data! 🕵',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        DISAPPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            gemini: 'Gemini is not approved for work use. 🚫',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        UNDEFINED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            gemini: 'Policy for Gemini is undefined. ⚠️',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
      },
    },
    {
      id: 'perplexity',
      displayName: 'Perplexity',
      pattern: 'https://www.perplexity.ai/',
      currentStatus: 'DISAPPROVED',
      templates: {
        APPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            perplexity: 'Enter a prompt for Perplexity... just no secrets, keys, or customer data! 🕵',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        DISAPPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            perplexity: 'Perplexity is not approved for work use. 🚫',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        UNDEFINED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            perplexity: 'Policy for Perplexity is undefined. ⚠️',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
      },
    },
    {
      id: 'copilot',
      displayName: 'Microsoft Copilot',
      pattern: 'https://copilot.microsoft.com/',
      currentStatus: 'APPROVED',
      templates: {
        APPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            copilot: 'Enter a prompt for Copilot... just no secrets, keys, or customer data! 🕵',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        DISAPPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            copilot: 'Microsoft Copilot is not approved for work use. 🚫',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        UNDEFINED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            copilot: 'Policy for Microsoft Copilot is undefined. ⚠️',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
      },
    },
    {
      id: 'claude',
      displayName: 'Claude',
      pattern: 'https://claude.ai/new',
      currentStatus: 'APPROVED',
      templates: {
        APPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            claude: 'Enter a prompt for Claude... just no secrets, keys, or customer data! 🕵',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        DISAPPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            claude: 'Claude is not approved for work use. 🚫',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        UNDEFINED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            claude: 'Policy for Claude is undefined. ⚠️',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
      },
    },
    {
      id: 'grok',
      displayName: 'Grok',
      pattern: 'https://grok.com/',
      currentStatus: 'APPROVED',
      templates: {
        APPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            grok: 'Enter a prompt for Grok... just no secrets, keys, or customer data! 🕵',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        DISAPPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            grok: 'Grok is not approved for work use. 🚫',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        UNDEFINED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            grok: 'Policy for Grok is undefined. ⚠️',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
      },
    },
    {
      id: 'deepseek',
      displayName: 'DeepSeek',
      pattern: 'https://chat.deepseek.com/',
      currentStatus: 'APPROVED',
      templates: {
        APPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            deepseek: 'Enter a prompt for DeepSeek... just no secrets, keys, or customer data! 🕵',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        DISAPPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            deepseek: 'DeepSeek is not approved for work use. 🚫',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        UNDEFINED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            deepseek: 'Policy for DeepSeek is undefined. ⚠️',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
      },
    },
    {
      id: 'poe',
      displayName: 'Poe',
      pattern: 'https://poe.com/',
      currentStatus: 'APPROVED',
      templates: {
        APPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            poe: 'Enter a prompt for Poe... just no secrets, keys, or customer data! 🕵',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        DISAPPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            poe: 'Poe is not approved for work use. 🚫',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        UNDEFINED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            poe: 'Policy for Poe is undefined. ⚠️',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
      },
    },
    {
      id: 'mistral',
      displayName: 'Mistral',
      pattern: 'https://chat.mistral.ai/',
      currentStatus: 'APPROVED',
      templates: {
        APPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            mistral: 'Enter a prompt for Le Chat... just no secrets, keys, or customer data! 🕵',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        DISAPPROVED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            mistral: 'Mistral is not approved for work use. 🚫',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
        UNDEFINED: {
          colors: { border: '#7C4DFF', text: '#7C4DFF' },
          placeholders: {
            mistral: 'Policy for Mistral is undefined. ⚠️',
          },
          bestPracticesHtml: DEFAULT_BEST_PRACTICES_HTML,
          limpModeText: DEFAULT_LIMP_MODE_TEXT,
        },
      },
    },
  ],
};